import { groupDisconnectedChildren } from './groupDisconnectedChildren';
import { divideExcessiveChildren } from './divideExcessiveChildren';
import {
  RelationshipsLink,
  RelationshipsNode,
  RelationshipsViewRenderedGraph,
  RelationshipsViewRenderingData,
  RelationshipsViewSourceDataProps,
} from '../types';
import { ROOT_LEAF_GROUP_ID } from '../consts';
import { SYNTHETIC_NODE_DEFAULTS } from './consts';
import { reduceLinks } from './reduceLinks';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getComponentTypesInfo,
  GraphViewDataModel,
  PureGraphEdge,
  RelationshipDiagramsViewInterface,
} from '@ardoq/graph';
import { isTruthy } from '@ardoq/common-helpers';
import { GraphNodesToRelationshipsNodesReducerState } from 'tabview/relationshipDiagrams/types';
import { reduceNodes } from './reduceNodes';
import { graphEdgesToReferenceTypes } from 'tabview/relationshipDiagrams/graphEdgesToReferenceTypes';
import {
  augmentNodeWithLinks,
  buildNodeLinksMap,
  partitionGraphItemsHierarchy,
} from 'tabview/relationshipDiagrams/viewModel$/utils';
import {
  hasDefaultGreyColorReferenceFilter,
  hasDefaultGreyColorComponentFilter,
  maybeGrayOutTypes,
} from 'models/utils/filterUtils';
import { ROOT_NODE_ID } from 'tabview/relationshipDiagrams/consts';
import { ArdoqId, FormattingFilter } from '@ardoq/api-types';
import type { ComponentRepresentationData } from '@ardoq/basic-components';

/**
 * When there are multiple ungrouped components they need a synthetic container
 * so they appear as a single cluster alongside groups.
 */
const insertUngroupedComponentsBundleNode = (
  ungroupedComponents: ComponentGraphNode[],
  parent: RelationshipsNode | null
): RelationshipsNode | null => {
  const syntheticUngroupedComponentsBundle =
    ungroupedComponents.length > 1
      ? {
          ...SYNTHETIC_NODE_DEFAULTS,
          id: ROOT_LEAF_GROUP_ID,
          parent,
          children: null,
        }
      : null;

  if (syntheticUngroupedComponentsBundle && parent) {
    // self-register in the parent descendancy
    parent.children ??= [];
    parent.children.push(syntheticUngroupedComponentsBundle);
  }
  return syntheticUngroupedComponentsBundle;
};

type GetSingleTopLevelNodeArgs = {
  topLevelGroups: ComponentGraphGroup[];
  groupNodeMap: Map<string, RelationshipsNode>;
  topLevelGraphNodes: ComponentGraphNode[];
  ungroupedNodeMap: Map<string, RelationshipsNode>;
};

const getSingleTopLevelNode = ({
  topLevelGroups,
  groupNodeMap,
  topLevelGraphNodes,
  ungroupedNodeMap,
}: GetSingleTopLevelNodeArgs): RelationshipsNode | null =>
  topLevelGroups.length === 1
    ? (groupNodeMap.get(topLevelGroups[0].id) ?? null)
    : topLevelGraphNodes.length === 1
      ? (ungroupedNodeMap.get(topLevelGraphNodes[0].id) ?? null)
      : null;

type BuildRenderedGraphSourceArgs = GraphViewDataModel<
  ComponentGraphNode,
  ComponentGraphGroup,
  PureGraphEdge
> & {
  viewInstanceId: string;
  viewInterface: RelationshipDiagramsViewInterface;
  isViewpointMode: boolean;
  formattingFilters: FormattingFilter[];
  colorFilterIdsByComponentId: Map<ArdoqId, string[]>;
  colorFilterIdsByReferenceId: Map<ArdoqId, string[]>;
  context: { componentId: string };
  componentRepresentationDataByComponentId: Map<
    ArdoqId,
    ComponentRepresentationData | null
  >;
};

const buildRenderedGraphData = (
  sourceData: BuildRenderedGraphSourceArgs
): RelationshipsViewRenderedGraph => {
  const {
    groups: graphGroups,
    nodes: graphNodes,
    edges: graphEdges,
    viewInstanceId,
    viewInterface,
    colorFilterIdsByComponentId,
    colorFilterIdsByReferenceId,
    componentRepresentationDataByComponentId,
    context,
  } = sourceData;

  const {
    rootGroups: topLevelGroups,
    rootGraphNodes: topLevelGraphNodes,
    componentIds,
    groupChildren,
    allGraphEdges,
  } = partitionGraphItemsHierarchy({ graphGroups, graphNodes, graphEdges });

  const syntheticTreeRoot =
    topLevelGroups.length + topLevelGraphNodes.length > 1
      ? ({
          ...SYNTHETIC_NODE_DEFAULTS,
          id: ROOT_NODE_ID,
          parent: null,
          children: [],
        } satisfies RelationshipsNode)
      : null;

  const syntheticUngroupedComponentsBundle =
    insertUngroupedComponentsBundleNode(topLevelGraphNodes, syntheticTreeRoot);

  const typeModelIdByComponentId = new Map(
    componentIds
      .map(id => {
        const typeId = viewInterface.getComponentTypeId(id);
        return typeId ? ([id, typeId] as const) : null;
      })
      .filter(isTruthy)
  );

  const sharedReduceState = {
    groupChildren,
    viewInstanceId,
    colorFilterIdsByComponentId,
    contextComponentId: context.componentId || undefined,
    typeModelIdByComponentId,
    componentRepresentationDataByComponentId,
  };
  // Append the top level groups to the synthetic root
  const { map: groupNodeMap } = topLevelGroups.reduce<
    GraphNodesToRelationshipsNodesReducerState<RelationshipsNode>
  >(reduceNodes, {
    map: new Map(),
    parent: syntheticTreeRoot,
    ...sharedReduceState,
  });
  // Append the ungrouped nodes to the syntheticUngroupedComponentsBundle;
  // If there is only one ungrouped component, there will be no synthetic group,
  // and we append the to the root node directly.
  const { map: ungroupedNodeMap } = topLevelGraphNodes.reduce<
    GraphNodesToRelationshipsNodesReducerState<RelationshipsNode>
  >(reduceNodes, {
    map: new Map(),
    parent: syntheticUngroupedComponentsBundle ?? syntheticTreeRoot,
    ...sharedReduceState,
  });

  const nodeMap = new Map([...groupNodeMap, ...ungroupedNodeMap]);

  const rootNode =
    syntheticTreeRoot ??
    syntheticUngroupedComponentsBundle ??
    getSingleTopLevelNode({
      topLevelGroups,
      groupNodeMap,
      topLevelGraphNodes,
      ungroupedNodeMap,
    });

  // Apply grey color to reference types and component types when "Keep original color" is unchecked in viewpoint mode
  const { formattingFilters } = sourceData;
  const shouldApplyGreyToReferences =
    sourceData.isViewpointMode &&
    hasDefaultGreyColorReferenceFilter(formattingFilters);
  const shouldApplyGreyToComponents =
    sourceData.isViewpointMode &&
    hasDefaultGreyColorComponentFilter(formattingFilters);

  const { referenceTypes: allEdgesReferenceTypes, legendReferenceTypes } =
    graphEdgesToReferenceTypes(
      viewInterface,
      allGraphEdges,
      shouldApplyGreyToReferences
    );

  const { links } = allGraphEdges.reduce(reduceLinks, {
    links: [],
    linksBySourceAndTarget: new Map(),
    nodeMap,
    allEdgesReferenceTypes,
    colorFilterIdsByReferenceId,
  });

  const nodeLinks = buildNodeLinksMap<RelationshipsNode, RelationshipsLink>(
    links
  );

  if (rootNode) {
    augmentNodeWithLinks(rootNode, nodeLinks);
    divideExcessiveChildren(rootNode);
  }

  const { legendComponentTypes: legendComponentTypesBase } =
    getComponentTypesInfo(componentIds);
  const legendComponentTypes = maybeGrayOutTypes(
    legendComponentTypesBase,
    shouldApplyGreyToComponents,
    'borderColor'
  );
  const isViewEmpty = rootNode === null;
  const disconnectedChildren = rootNode
    ? groupDisconnectedChildren([], rootNode)
    : null;

  return {
    rootNode,
    links,
    componentTypes: legendComponentTypes,
    referenceModelTypes: legendReferenceTypes,
    groups: graphGroups,
    isViewEmpty,
    disconnectedChildren,
  };
};

export type BuildRelationshipsViewRenderingDataSourceArgs =
  BuildRenderedGraphSourceArgs & RelationshipsViewSourceDataProps;

export const buildRelationshipsViewRenderingData = (
  sourceData: BuildRelationshipsViewRenderingDataSourceArgs
): RelationshipsViewRenderingData => ({
  ...sourceData,
  ...buildRenderedGraphData(sourceData),
});
